import type { ProgressImage as ProgressImageProps } from '@web/graphql-components'
import { useQuery } from '@tanstack/react-query'
import { ProgressImage as ProgressImageComponent } from '@web/components/page-components/ProgressImage'
import { ProgressBarElementSettingItem, usePageStore } from '../../libs/hooks/usePageStore'
import SubComponent from './SubComponent'
import { toSearchParams } from 'apps/fe-donation/utils'
import { ProgressBarType } from '@web/components/page-components/ProgressBar/progressBarUtils'

const ProgressImage = (component: ProgressImageProps) => {
  const charityId = usePageStore((state) => state.charityId)
  const campaignId = usePageStore((state) => state.campaignId)
  const isLiveMode = usePageStore((state) => state.isLiveMode)
  const profileId = usePageStore((state) => state.profileId)
  const profileData = usePageStore((state) => state.profileData)
  const progressIds = usePageStore((state) => state.elementSettings?.progressBar?.pageIds ?? [])
  const isProfileProgressImage = component.styles?.options?.type === ProgressBarType.Profile
  const campaignGoal = usePageStore(
    (state) =>
      (state.elementSettings?.progressBar as ProgressBarElementSettingItem)?.goals?.reduce(
        (acc, curr) => acc + curr,
        0,
      ) ?? 0,
  )
  const goal =
    isProfileProgressImage && profileId && profileData?.goal ? profileData.goal : campaignGoal
  const defaultPageId = usePageStore((state) => state.pageId ?? '')

  const { data: amount = 0 } = useQuery({
    queryKey: ['progressBarAmount', charityId, campaignId, progressIds, component.styles?.options?.type, profileId],
    queryFn: async () => {
      const amount = await fetch(
        `/api/progress-bar?${toSearchParams({
          campaignId,
          pageIds: progressIds.map((x) => x ?? '') || [defaultPageId],
          isLiveMode,
          profileId: isProfileProgressImage ? profileId : undefined,
        })}`,
      )
        .then((res) => res.json())
        .then((data) => data.amount)
        .catch(() => 0)
      return amount
    },
    refetchOnMount: false,
    enabled: !!charityId && !!campaignId,
    refetchInterval: component?.styles?.options?.isLiveUpdateProgressBar ? 10000 : undefined,
  })

  const progress = Math.floor(amount && goal ? (Math.max(0, amount) / goal) * 100 : 0)

  return <ProgressImageComponent {...component} progress={progress} SubComponent={SubComponent} />
}

export default ProgressImage
